/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import type { LinkProps } from 'react-router';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button } from '@coursera/cds-core';
import type { IconProps } from '@coursera/cds-core';
import { ReplyIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/index';

import formatCount from '../../utils/formatCount';
import { extractForumPostId } from '../forumsV2-ForumThreads/__helpers__/forumPostDataHelpers';
import type { AuthorProfile } from '../forumsV2-ForumThreads/__providers__/ForumPostDataProvider/__types__';

type Props = {
  creator?: AuthorProfile;
  replies?: number;
  ariaLabel?: string;
  forumQuestionId: string;
  forumId?: string;
  onClick?: () => void;
  to?: string;
  componentType?: React.ElementType<any>;
  role?: string;
};

interface TrackedReplyButtonProps extends Omit<LinkProps, 'to'> {
  icon: React.ReactElement<IconProps>;
  message: string;
  onClick: () => void;
  ariaLabel: string;
  role?: string;
  to?: string;
  componentType?: React.ElementType<any>;
}

const TrackedReplyButton = withSingleTracked({
  type: 'BUTTON',
})(({ icon, message, onClick, ariaLabel, componentType, ...props }: TrackedReplyButtonProps) => {
  if (componentType) {
    return (
      <Button
        variant="ghost"
        type="button"
        className="rc-forumsV2_ReplyButton"
        aria-label={ariaLabel}
        icon={icon}
        iconPosition="before"
        component={componentType}
        {...props}
        size="small"
      >
        {message}
      </Button>
    );
  } else {
    return (
      <Button
        size="small"
        variant="ghost"
        type="button"
        className="rc-forumsV2_ReplyButton"
        onClick={onClick}
        aria-label={ariaLabel}
        icon={icon}
        iconPosition="before"
        role="button"
      >
        {message}
      </Button>
    );
  }
});

class ReplyButton extends React.LegacyComponentWithChildren<Props> {
  static contextTypes = {
    userId: PropTypes.number,
  };

  declare context: LegacyContextType<typeof ReplyButton.contextTypes>;

  render() {
    const message =
      this.props.replies && this.props.replies > 0
        ? _t('Reply #{replies}', { replies: formatCount(this.props.replies) })
        : _t('Reply');
    const forumQuestionId = extractForumPostId(this.props.forumQuestionId);

    return (
      <TrackedReplyButton
        data-testid="forums-reply-button"
        trackingName="discussion_forum_reply_button"
        trackingData={{ forumQuestionId }}
        icon={<ReplyIcon size="small" />}
        message={message}
        onClick={() => this.props?.onClick && this.props.onClick()}
        ariaLabel={this.props.ariaLabel || _t('reply button')}
        {...this.props}
      >
        {this.props.children}
      </TrackedReplyButton>
    );
  }
}

export default ReplyButton;
